import { useCallback, useMemo, useEffect, useState } from 'react';
import { MoneyInput } from './money-input';
import { FormInputCommonProps } from './types';
import { Listbox } from '@headlessui/react';
import { isArray } from 'lodash';
import SelectArrow from '../../images/select-arrow.svg';
import { classNames } from '../../utils';
import { CheckboxInput } from './checkbox-input';
import { RadioGroupInput } from './radio-group-input';
import { twMerge } from 'tailwind-merge';
import { get, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { LabelTooltip } from './input-row';
import { ResponsiveCostSlider } from './responsive-cost-slider';
import { RangeSlider } from './range-slider';

interface DropdownRangeSliderInputProps extends FormInputCommonProps {
  multiple?: boolean;
  value: number[] | undefined | null;
  onChange: (value: number[]) => void;
  label: React.ReactNode;
  changeOnClose?: boolean;
  tooltipText?: string;
  tooltipClassName?: string;
  iconClassName?: string;
  placeholder?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  labelClassName?: string;
  rangeClassName?: string;
  min: number;
  max: number;
  step: number;
  sliderHeaderLabel?: string;
  marker?: number;
  leftText?: React.ReactNode;
  rightText?: React.ReactNode;
  labelWrapperClassName?: string;
  openWrapperClassName?: string;
  selectArrowIcon?: React.ReactNode;
  labelTooltip?: React.ReactNode;
  hidePointsCreditsLabel?: boolean;
  formatter?: (val: number) => string;
}

const WatchOpenStatus: React.FC<{
  open?: boolean;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ open, onClose, children }) => {
  const [hasBeenOpen, setHasBeenOpen] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setHasBeenOpen(true);
    } else {
      if (hasBeenOpen) {
        onClose();
      }
    }
  }, [open, hasBeenOpen, setHasBeenOpen]);
  return <>{children}</>;
};

function areTuplesEqual<T>(tuple1: T[], tuple2: T[]): boolean {
  if (tuple1.length !== tuple2.length) {
    return false;
  }

  return tuple1.every((value, index) => {
    if (Array.isArray(value) && Array.isArray(tuple2[index])) {
      return areTuplesEqual(value, tuple2[index] as any[]);
    }
    return value === tuple2[index];
  });
}

export const DropdownRangeSliderInput: React.FC<
  DropdownRangeSliderInputProps
> = ({
  name,
  multiple,
  value,
  changeOnClose,
  onChange,
  label,
  disabled,
  className,
  tooltipText,
  tooltipClassName,
  iconClassName,
  dropdownClassName,
  labelClassName,
  step,
  min,
  max,
  sliderHeaderLabel,
  marker,
  leftText,
  rightText,
  labelWrapperClassName,
  openWrapperClassName,
  selectArrowIcon,
  hidePointsCreditsLabel,
  labelTooltip,
  rangeClassName,
  formatter = (val) => val.toString(),
}) => {
  const methods = useFormContext();
  const errors = methods?.formState.errors;
  const error = methods && name ? get(errors, name) : null;

  const [localValue, setLocalValue] = useState<number[] | undefined | null>(
    value
  );

  const onClose = useCallback(() => {
    if (!localValue) return;
    const valueIsEqual = areTuplesEqual(value ?? [0, 0], localValue);
    if (changeOnClose && !valueIsEqual) {
      onChange(localValue);
    }
  }, [localValue, onChange]);

  useEffect(() => {
    setLocalValue(value);
  }, [value, setLocalValue]);

  const checkPointsOrCredits = () => {
    if (!localValue) {
      return 'Points/Credits';
    } else if (localValue[0] > 0 && localValue[1] > 0) {
      return 'Points';
    } else if (localValue[0] < 0 && localValue[1] < 0) {
      return 'Credits';
    } else {
      return 'Points/Credits';
    }
  };

  const pointsOrCreditsLabel = hidePointsCreditsLabel
    ? ''
    : `${checkPointsOrCredits()}: `;

  return (
    <div className={twMerge('block w-full', className)}>
      {/* @ts-ignore */}
      <Listbox
        disabled={disabled}
        value={localValue || []}
        onChange={() => {
          changeOnClose ? setLocalValue : onChange;
        }}
        multiple={multiple}
      >
        {/* @ts-ignore */}
        {({ open }) => {
          return (
            <WatchOpenStatus onClose={onClose} open={open}>
              <Listbox.Button className="w-full">
                {/* @ts-ignore */}
                {(bag) => {
                  return (
                    <div
                      className={twMerge(
                        'typo-body-small bg-hardwood-100 text-hardwood-700 hover:bg-hardwood-200 active:bg-hardwood-300 relative flex h-[28px] w-auto items-center overflow-hidden text-ellipsis rounded-[39px] pl-3 pr-[20px] text-left md:pl-[8px]',
                        disabled
                          ? 'bg-hardwood-50 pointer-events-none cursor-default'
                          : '',
                        labelWrapperClassName ? labelWrapperClassName : '',
                        bag.open && openWrapperClassName
                          ? openWrapperClassName
                          : ''
                      )}
                    >
                      <div className="flex items-center">
                        {tooltipText ? (
                          <LabelTooltip
                            tooltipClassName={tooltipClassName}
                            iconClassName={iconClassName}
                          >
                            {tooltipText}
                          </LabelTooltip>
                        ) : null}
                        <p className="flex items-center mb-0 truncate lg:w-[225px]">
                          <span
                            className={classNames(
                              'mr-3 font-bold',
                              labelClassName
                            )}
                          >
                            {label}
                          </span>
                          {labelTooltip ? labelTooltip : null}
                        </p>
                      </div>
                      {selectArrowIcon ? (
                        <div
                          className={classNames(
                            'absolute right-[6px] top-[14px] translate-y-[-50%]  transition-all',
                            bag.open ? 'rotate-180' : ''
                          )}
                        >
                          {selectArrowIcon}
                        </div>
                      ) : (
                        <img
                          alt="select-arrow"
                          className={classNames(
                            'absolute right-[10px] top-[50%] h-[6px] w-[10px] translate-y-[-50%]  transition-all',
                            bag.open ? `rotate-180` : ''
                          )}
                          src={SelectArrow}
                        />
                      )}
                    </div>
                  );
                }}
              </Listbox.Button>
              {open ? (
                <div
                  className={classNames(
                    'absolute z-[9999] mt-[6px] flex max-h-[286px] w-auto flex-col overflow-auto rounded-[4px] bg-white py-[12px] px-[16px] shadow-md md:w-[350px]',
                    dropdownClassName
                  )}
                >
                  <div className="typo-body-small text-sidewalk-900">
                    {sliderHeaderLabel ? sliderHeaderLabel : ''}
                  </div>
                  <RangeSlider
                    value={localValue ?? [0, 0]}
                    step={step}
                    min={min}
                    max={max}
                    marker={marker}
                    className={rangeClassName}
                    valueDisplay={(val, isDragging) => {
                      let value = localValue;

                      if (val && isDragging) {
                        value = val;
                      }

                      return (
                        <div className="flex items-center mb-1">
                          <div className="flex flex-row">
                            <div className="typo-body-small text-sidewalk-900">
                              {pointsOrCreditsLabel}
                              {value === null || value === undefined
                                ? ''
                                : `${
                                    val[0] === null ? '' : formatter?.(val[0])
                                  } ${
                                    val[0] === null && val[1] === null
                                      ? ''
                                      : '-'
                                  } ${
                                    val[1] === null ? '' : formatter?.(val[1])
                                  }`}
                            </div>
                          </div>
                        </div>
                      );
                    }}
                    onChange={async (val) => {
                      setLocalValue(val);
                      if (onChange && !changeOnClose) {
                        onChange(val);
                      }
                    }}
                    sliderClassName="w-full md:w-[232px] lg:w-full"
                    sliderValueWrapperClassName={
                      'flex-col flex-col-reverse gap-1'
                    }
                    leftText={leftText}
                    rightText={rightText}
                    valueClassName={'w-full'}
                  />
                </div>
              ) : null}
            </WatchOpenStatus>
          );
        }}
      </Listbox>
      {name && error ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => {
            return (
              <div className="typo-body-small text-hydrant-500 absolute">
                {message}
              </div>
            );
          }}
        />
      ) : null}
    </div>
  );
};
